import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AgentTodoItem, AgentTodoList, AgentTodoStatus } from '@platfio/interfaces/platfio';

@Component({
  selector: 'app-thread-todo-popover',
  templateUrl: './thread-todo-popover.component.html',
  styleUrl: './thread-todo-popover.component.scss'
})
export class ThreadTodoPopoverComponent implements AfterViewInit, OnChanges {
  @Input() todoList: AgentTodoList | null | undefined;
  @Input() lightTheme = false;
  @Input() defaultExpanded = true;
  @Input() debugMode = false;

  @Output() deleteTodoItem = new EventEmitter<AgentTodoItem>();
  @Output() editTodoItem = new EventEmitter<AgentTodoItem>();
  @Output() resetTodoList = new EventEmitter<void>();

  expanded = true;
  editMode = false;
  editingItemId: string | null = null;
  editTitle = '';
  editNotes = '';
  editStatus: AgentTodoStatus = 'pending';
  readonly statusOptions: Array<{ value: AgentTodoStatus; label: string }> = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'waiting_for_user', label: 'Waiting' }
  ];
  private userToggled = false;

  ngAfterViewInit(): void {
    this.applyResponsiveDefault();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultExpanded'] && !this.userToggled) {
      this.applyResponsiveDefault();
    }

    if (changes['debugMode'] && !this.debugMode) {
      this.editMode = false;
      this.editingItemId = null;
    }
  }

  get items(): AgentTodoItem[] {
    return Array.isArray(this.todoList?.items)
      ? this.todoList.items.filter(item => !!item?.id && !!item?.title)
      : [];
  }

  get visibleItems(): AgentTodoItem[] {
    return this.expanded ? this.items : this.items.slice(0, 4);
  }

  get completedCount(): number {
    return this.items.filter(item => item.status === 'completed').length;
  }

  get totalCount(): number {
    return this.items.length;
  }

  get hasMoreItems(): boolean {
    return this.items.length > this.visibleItems.length;
  }

  toggleExpanded(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.userToggled = true;
    this.expanded = !this.expanded;
  }

  onToggleEditMode(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.editingItemId = null;
    }
  }

  onDeleteTodoItem(item: AgentTodoItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.deleteTodoItem.emit(item);
  }

  onEditTodoItem(item: AgentTodoItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.editingItemId = item.id;
    this.editTitle = item.title || '';
    this.editNotes = item.notes || '';
    this.editStatus = item.status || 'pending';
  }

  onCancelEdit(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.editingItemId = null;
  }

  onSaveEdit(item: AgentTodoItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const title = this.editTitle.trim();
    if (!title) {
      return;
    }

    const notes = this.editNotes.trim();
    this.editTodoItem.emit({
      ...item,
      title,
      status: this.editStatus,
      notes: notes || undefined
    });
    this.editingItemId = null;
  }

  onResetTodoList(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.resetTodoList.emit();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  getStatusIcon(item: AgentTodoItem): string {
    if (item.status === 'completed') {
      return 'fa-solid fa-check';
    }

    if (item.status === 'in_progress') {
      return 'fa-solid fa-minus';
    }

    if (item.status === 'blocked') {
      return 'fa-solid fa-exclamation';
    }

    if (item.status === 'waiting_for_user') {
      return 'fa-regular fa-clock';
    }

    return '';
  }

  getTodoTooltip(item: AgentTodoItem): string {
    return typeof item.notes === 'string' ? item.notes.trim() : '';
  }

  isEditing(item: AgentTodoItem): boolean {
    return this.editingItemId === item.id;
  }

  trackByTodoId(_index: number, item: AgentTodoItem): string {
    return item.id;
  }

  private applyResponsiveDefault(): void {
    this.expanded = this.defaultExpanded;
  }
}
